// ProductTemplates.jsx
// Template selection modal for quick product creation
// ALIGNED with ProductForm data structures

import React, { useState, useMemo, memo } from "react";
import {
  X,
  Search,
  Sparkles,
  Package,
  Gem,
  CircleDot,
  Plus,
  Trash2,
  Crown,
  Heart,
} from "lucide-react";

// ============= ALIGNED TEMPLATES WITH PRODUCTFORM STRUCTURE =============
const BUILT_IN_TEMPLATES = [
  {
    id: "gold-ring-22k",
    name: "Gold Ring 22K",
    description: "Standard 22K gold ring",
    icon: CircleDot,
    category: "Rings",
    data: {
      type: "GOLD",
      subType: "RING",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT", // ALIGNED: DRAFT as default
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "gold-necklace-22k",
    name: "Gold Necklace 22K",
    description: "Traditional 22K necklace",
    icon: Crown,
    category: "Necklaces",
    data: {
      type: "GOLD",
      subType: "NECKLACE",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "diamond-ring-18k",
    name: "Diamond Ring 18K",
    description: "Diamond 18K white gold ring",
    icon: Gem,
    category: "Rings",
    data: {
      type: "STUDDED",
      subType: "RING",
      purity: "18K",
      metalColor: "WHITE",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: true,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      isFeatured: true,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "silver-anklet",
    name: "Silver Anklet 925",
    description: "Sterling silver anklet",
    icon: CircleDot,
    category: "Anklets",
    data: {
      type: "SILVER",
      subType: "ANKLET",
      purity: "925",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "MACHINE_MADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "gold-bangle-22k",
    name: "Gold Bangle 22K",
    description: "Traditional 22K bangle",
    icon: CircleDot,
    category: "Bangles",
    data: {
      type: "GOLD",
      subType: "BANGLE",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "gold-chain-22k",
    name: "Gold Chain 22K",
    description: "22K gold chain",
    icon: Package,
    category: "Chains",
    data: {
      type: "GOLD",
      subType: "CHAIN",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "UNISEX",
      status: "DRAFT",
      craftType: "MACHINE_MADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "gold-mangalsutra",
    name: "Gold Mangalsutra",
    description: "Traditional mangalsutra",
    icon: Heart,
    category: "Mangalsutra",
    data: {
      type: "GOLD",
      subType: "MANGALSUTRA",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "gold-earring-22k",
    name: "Gold Earring 22K",
    description: "Traditional 22K gold earring",
    icon: Gem,
    category: "Earrings",
    data: {
      type: "GOLD",
      subType: "EARRING",
      purity: "22K",
      metalColor: "YELLOW",
      gender: "FEMALE",
      status: "DRAFT",
      craftType: "HANDMADE",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
  {
    id: "platinum-ring",
    name: "Platinum Ring PT950",
    description: "Premium platinum ring",
    icon: CircleDot,
    category: "Rings",
    data: {
      type: "PLATINUM",
      subType: "RING",
      purity: "PT950",
      metalColor: "WHITE",
      gender: "UNISEX",
      status: "DRAFT",
      craftType: "CAST",
      grossWeight: "",
      netWeight: "",
      viewAccess: ["B2C_WEBSITE", "B2C_APP"],
      stock: {
        totalQuantity: 1,
        reservedQuantity: 0,
        availableQuantity: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        locationStock: [],
      },
      hasStones: false,
      stones: [],
      totalStoneWeight: 0,
      stoneCharges: 0,
      isExclusive: true,
      hallmark: {
        isHallmarked: false,
        huid: "",
        centerCode: "",
        year: new Date().getFullYear().toString(),
        jewellerCode: "",
        purity: "",
      },
    },
  },
];

const STORAGE_KEY = "multiProductCreator_customTemplates";

const ProductTemplates = memo(({ onSelect, onClose, currentData }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [customTemplates, setCustomTemplates] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  const categories = useMemo(() => {
    const cats = new Set(["All"]);
    [...BUILT_IN_TEMPLATES, ...customTemplates].forEach(
      (t) => t.category && cats.add(t.category)
    );
    return Array.from(cats);
  }, [customTemplates]);

  const filteredTemplates = useMemo(() => {
    const all = [
      ...BUILT_IN_TEMPLATES.map((t) => ({ ...t, isBuiltIn: true })),
      ...customTemplates.map((t) => ({ ...t, isBuiltIn: false })),
    ];
    return all.filter((t) => {
      const matchSearch =
        !searchQuery ||
        t.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCategory =
        selectedCategory === "All" || t.category === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [searchQuery, selectedCategory, customTemplates]);

  const saveAsTemplate = () => {
    if (!newTemplateName.trim()) return;

    // Create template with aligned structure
    const newTemplate = {
      id: `custom-${Date.now()}`,
      name: newTemplateName.trim(),
      description: "Custom template",
      category: "Custom",
      data: {
        ...currentData,
        name: "", // Clear name for template
        productId: "", // Clear identifiers
        sku: "",
      },
      createdAt: Date.now(),
    };

    const updated = [...customTemplates, newTemplate];
    setCustomTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setShowSaveModal(false);
    setNewTemplateName("");
  };

  const deleteTemplate = (id) => {
    const updated = customTemplates.filter((t) => t.id !== id);
    setCustomTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg">
              <Sparkles size={18} className="text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 text-sm">
                Product Templates
              </h2>
              <p className="text-xs text-gray-500">Start with a template</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="px-4 py-3 border-b border-gray-100 space-y-2.5">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
            />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex-shrink-0 px-2.5 py-1 text-xs rounded-md transition-colors ${
                  selectedCategory === cat
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Save as Template */}
            {currentData?.type && (
              <button
                onClick={() => setShowSaveModal(true)}
                className="group flex items-center gap-3 p-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50"
              >
                <div className="p-2.5 bg-gray-100 rounded-lg group-hover:bg-gray-200">
                  <Plus size={20} className="text-gray-500" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-700 text-sm">
                    Save as Template
                  </p>
                  <p className="text-xs text-gray-500">
                    Create from current product
                  </p>
                </div>
              </button>
            )}

            {/* Template Cards */}
            {filteredTemplates.map((template) => {
              const Icon = template.icon || Package;
              return (
                <div
                  key={template.id}
                  className="group relative flex items-start gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer"
                  onClick={() => onSelect(template)}
                >
                  <div
                    className={`p-2.5 rounded-lg ${
                      template.isBuiltIn
                        ? "bg-gradient-to-br from-gray-700 to-gray-900"
                        : "bg-gradient-to-br from-amber-400 to-amber-600"
                    }`}
                  >
                    <Icon size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {template.name}
                      </p>
                      {template.isBuiltIn && (
                        <span className="px-1 py-0.5 bg-gray-200 text-gray-600 text-[10px] font-medium rounded">
                          BUILT-IN
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {template.description}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="px-1.5 py-0.5 bg-white text-gray-600 text-[10px] rounded">
                        {template.data.type}
                      </span>
                      <span className="px-1.5 py-0.5 bg-white text-gray-600 text-[10px] rounded">
                        {template.data.purity}
                      </span>
                      {template.data.subType && (
                        <span className="px-1.5 py-0.5 bg-white text-gray-600 text-[10px] rounded">
                          {template.data.subType.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                  </div>
                  {!template.isBuiltIn && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template.id);
                      }}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <Package size={40} strokeWidth={1} className="mb-2" />
              <p className="text-sm">No templates found</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4"
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-xs p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">
              Save as Template
            </h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Template name..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900 mb-3"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 py-2 text-gray-600 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveAsTemplate}
                disabled={!newTemplateName.trim()}
                className="flex-1 py-2 text-white bg-gray-900 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 text-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.15s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
});

ProductTemplates.displayName = "ProductTemplates";

export default ProductTemplates;
